import React, { useEffect, useState } from "react";
import {
    Box, Typography, Paper, Grid, TextField, CircularProgress,
    Stack, Avatar, Button, MenuItem, Card, CardContent, Checkbox, Divider
} from "@mui/material";
import { Print, LocationOn, Call, Email, VerifiedUser } from "@mui/icons-material";
import { collection, getDocs, doc, query, where, getDoc, orderBy } from "firebase/firestore";
import { db, auth } from "../../firebase";
import QRCode from "react-qr-code";
import Swal from "sweetalert2";

const THEME_RED = "#b71c1c";
const THEME_GOLD = "#D4AF37"; // Metallic Gold

function Idcardgeneration() {
    const [students, setStudents] = useState([]);
    const [filteredStudents, setFilteredStudents] = useState([]);
    const [classes, setClasses] = useState([]);
    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState("");
    const [selectedClass, setSelectedClass] = useState("All");
    const [selectedIds, setSelectedIds] = useState([]);
    const [activeYear, setactiveYear] = useState("");

    useEffect(() => {
        const init = async () => {
            const userDoc = await getDoc(doc(db, "users", auth.currentUser?.uid));
            const sId = userDoc.data()?.schoolId;
            const yearSnap = await getDocs(query(collection(db, "academicyears"), where("schoolId", "==", sId), where("status", "==", "Active")));
            const activeYear = yearSnap.empty ? null : yearSnap.docs[0].data().name;
            setactiveYear(activeYear)
            if (sId && activeYear) fetchData(sId, activeYear);
        };
        init();
    }, []);

    const fetchData = async (sId, year) => {
        try {
            setLoading(true);
            const courseQuery = query(collection(db, "courses"), orderBy("sequence", "asc"));
            const [courseSnap, admSnap, stdSnap] = await Promise.all([
                getDocs(courseQuery),
                getDocs(query(collection(db, "admission"), where("schoolId", "==", sId), where("academicYear", "==", year), where('status', "==", 'Active'))),
                getDocs(collection(db, "students"))
            ]);

            const courseMap = {};
            const classList = [];
            courseSnap.forEach(d => {
                courseMap[d.id] = d.data().name;
                classList.push({ id: d.id, name: d.data().name });
            });
            setClasses(classList);

            const admMap = {};
            admSnap.forEach(d => { admMap[d.data().studentId] = d.data(); });

            const merged = stdSnap.docs
                .map(d => ({ id: d.id, ...d.data() }))
                .filter(s => admMap[s.id])
                .map(s => ({
                    ...s,
                    className: courseMap[admMap[s.id].classId || admMap[s.id].class] || "N/A",
                    rollNo: admMap[s.id].rollno || "-",
                    grNo: admMap[s.id].grNo || s.grNo || "-",
                    fatherMobile: admMap[s.id].father_mobile1 || s.mobile || "-",
                    rollNo: admMap[s.id].rollno || "-",
                    address: admMap[s.id]?.father_currentAddress
                        ? `${admMap[s.id].father_currentAddress}, ${admMap[s.id].father_currentPin || ""}`
                        : "-"
                }));

            setStudents(merged);
            setFilteredStudents(merged);
        } catch (e) {
            Swal.fire("Error", "Data sync failed", "error");
        } finally { setLoading(false); }
    };

    const handleFilter = (txt, cls) => {
        setSearch(txt);
        setSelectedClass(cls);
        const term = txt.toLowerCase();
        const filtered = students.filter(s =>
            (cls === "All" || s.classId === cls) &&
            (`${s.firstname} ${s.surname}`.toLowerCase().includes(term) || s.rollNo.toString().includes(term))
        );
        setFilteredStudents(filtered);
    };

    const toggleSelect = (id) => {
        setSelectedIds(prev => prev.includes(id) ? prev.filter(i => i !== id) : [...prev, id]);
    };

    return (
        <Box sx={{ bgcolor: "#f5f5f5", minHeight: "100vh", "@media print": { p: 0, bgcolor: 'transparent' } }}>
            {/* Control UI */}
            <Box sx={{ "@media print": { display: 'none' } }}>
                <Stack direction="row" justifyContent="space-between" mb={3} alignItems="center">
                    <Typography variant="h5" fontWeight="900" color={THEME_RED}>Premium ID Generator</Typography>
                    <Button
                        variant="contained"
                        startIcon={<Print />}
                        disabled={selectedIds.length === 0}
                        onClick={() => window.print()}
                        sx={{ bgcolor: THEME_RED, px: 4, '&:hover': { bgcolor: '#8e0000' } }}
                    >
                        Export PDF ({selectedIds.length})
                    </Button>
                </Stack>

                <Paper sx={{ p: 1, mb: 4, borderRadius: 3 }}>
                    <Grid className="row" spacing={2}>
                        <Grid className="col-sm-4">
                            <TextField fullWidth size="small" label="Search Student..." value={search} onChange={e => handleFilter(e.target.value, selectedClass)} />
                        </Grid>
                        <Grid className="col-sm-4">
                            <Stack direction="row" spacing={1}>
                                <Button fullWidth variant="outlined" sx={{ color: THEME_RED, borderColor: THEME_RED }} onClick={() => setSelectedIds(filteredStudents.map(s => s.id))}>Select All</Button>
                                <Button fullWidth variant="outlined" color="error" onClick={() => setSelectedIds([])}>Clear</Button>
                            </Stack>
                        </Grid>
                    </Grid>
                </Paper>
            </Box>


            <div id="print-area">
                <div className="pass-card" >
                    <Box
                        className="print-grid"
                        sx={{
                            display: 'flex',
                            flexWrap: 'wrap',
                            gap: 3,
                            justifyContent: 'center',
                        }}
                    >
                        {loading ? <CircularProgress sx={{ color: THEME_RED }} /> : filteredStudents.filter(s => selectedIds.includes(s.id)).map(student => (
                            <Box key={student.id} sx={{ "@media print": { pageBreakInside: 'avoid', mb: '30px', display: 'flex', justifyContent: 'center' } }}>
                                <IDCardView student={student} activeYear={activeYear} />
                            </Box>
                        ))}
                    </Box>
                </div>
            </div>

            {/* List Selection Grid */}
            <Box sx={{ "@media print": { display: 'none' } }}>
                <Grid className="row" spacing={2}>
                    {filteredStudents.map(s => (
                        <Grid className="col-sm-3 mb-2" item xs={12} sm={6} md={3} key={s.id}>
                            <Card
                                variant="outlined"
                                sx={{
                                    cursor: 'pointer',
                                    borderColor: selectedIds.includes(s.id) ? THEME_RED : '#eee',
                                    bgcolor: selectedIds.includes(s.id) ? '#fff8f8' : 'white'
                                }}
                                onClick={() => toggleSelect(s.id)}
                            >
                                <CardContent sx={{ display: 'flex', alignItems: 'center', p: 1.5 }}>
                                    <Checkbox checked={selectedIds.includes(s.id)} size="small" sx={{ color: THEME_RED, '&.Mui-checked': { color: THEME_RED } }} />
                                    <Avatar src={s.photoURL} sx={{ mx: 1 }} />
                                    <Box>
                                        <Typography variant="subtitle2" noWrap>{s.firstname} {s.surname}</Typography>
                                        <Typography variant="caption" color="textSecondary">Roll: {s.rollNo}</Typography>
                                    </Box>
                                </CardContent>
                            </Card>
                        </Grid>
                    ))}
                </Grid>
            </Box>
        </Box >
    );
}


const IDCardView = ({ student, activeYear }) => (

    <Paper className="id-card"
        elevation={10}
        sx={{
            width: "320px",
            height: "510px",
            borderRadius: "25px",
            overflow: "hidden",
            position: "relative",
            background: "#fff",
            border: `2px solid ${THEME_GOLD}`, // Gold outer border
            "@media print": { boxShadow: "none", border: "1px solid #ddd" }
        }}
    >
        {/* HEADER WITH WAVE DESIGN */}
        <Box sx={{
            height: "145px",
            background: `linear-gradient(135deg, ${THEME_RED} 0%, #7f0000 100%)`,
            position: 'relative',
            pt: 1,
            px: 1,
            zIndex: 1,
            clipPath: 'polygon(0 0, 100% 0, 100% 85%, 50% 100%, 0 85%)',
        }}>
            {/* Logo and Title Container */}
            <Stack direction="row" spacing={1.5} alignItems="center">
                <Box sx={{
                    bgcolor: 'white',
                    borderRadius: '20px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    width: '65px',
                    height: '50px',
                    boxShadow: '0 4px 10px rgba(0,0,0,0.3)',
                }}>
                    <img src="logo.jpeg" alt="Logo" style={{ width: "100%", height: "45px", borderRadius: 5 }} />
                </Box>

                <Box color="white">
                    <Typography sx={{ fontSize: '8.5px', fontWeight: 700, letterSpacing: 1, color: THEME_GOLD }}>
                        ABHINAV FOUNDATION'S
                    </Typography>
                    <Typography sx={{ fontSize: '11.5px', fontWeight: 900, lineHeight: 1.3, mb: 0.3, }}>
                        MOHAN MALI INTERNATIONAL SCHOOL & JR. COLLEGE
                    </Typography>
                    <Typography sx={{ fontSize: '7px', opacity: 0.9 }}>
                        Jath-Sangli Road, Alkud(S), Kavathe Mahankal , Dist - Sangli , MH - 416411
                    </Typography>
                    <Typography sx={{ fontSize: '7.5px', opacity: 0.9 }}>
                        Call - +91 9890555478 , +91 9021666565
                    </Typography>
                </Box>
            </Stack>
        </Box>

        {/* PHOTO SECTION - Z-INDEX OVER HEADER */}
        <Box sx={{
            display: 'flex',
            justifyContent: 'center',
            mt: "-65px",
            position: 'relative',
            zIndex: 10
        }}>
            <Box sx={{ position: 'relative' }}>
                <Avatar
                    src={student.photoURL}
                    sx={{
                        width: 90,
                        height: 90,
                        border: `4px solid white`,
                        outline: `2px solid ${THEME_GOLD}`,
                        boxShadow: '0 10px 25px rgba(0,0,0,0.2)',
                        bgcolor: '#f5f5f5'
                    }}
                />
                <Box sx={{
                    position: 'absolute',
                    bottom: 10,
                    right: 10,
                    bgcolor: '#4caf50',
                    color: 'white',
                    borderRadius: '50%',
                    width: 26,
                    height: 26,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    border: '2px solid white',
                    boxShadow: 3
                }}>
                    <VerifiedUser sx={{ fontSize: 16 }} />
                </Box>
            </Box>
        </Box>

        {/* STUDENT IDENTITY INFO */}
        <Box sx={{ textAlign: 'center', px: 2, mt: 1 }}>
            <Typography variant="h6" sx={{ fontWeight: 900, color: '#1a1a1a', letterSpacing: -0.5, mb: 0.5, textTransform: "uppercase" }}>
                {student.firstname}  {student.fathername} {student.surname}
            </Typography>

            <Box sx={{
                display: 'inline-block',
                background: `linear-gradient(90deg, ${THEME_RED} 0%, ${THEME_GOLD} 100%)`,
                px: 3, py: 0.5,
                borderRadius: '50px',
                mb: 2.5,
                boxShadow: '0 4px 10px rgba(183, 28, 28, 0.2)'
            }}>
                <Typography sx={{ fontSize: '11px', fontWeight: 900, color: '#fff', letterSpacing: 1 }}>
                    CLASS : {student.className}
                </Typography>
            </Box>

            <Grid className="row" spacing={1.5} sx={{ textAlign: 'left' }}>
                <Grid className="col-sm-6" item xs={7.5}>
                    <Stack spacing={1.5}>
                        <Stack spacing={1.5}>
                            <DataField label="GR Number" value={student.grno} />
                            <DataField label="Roll Number" value={student.rollNo} />
                            <DataField label="Parent Mobile" value={student.fatherMobile} />
                            <DataField label="Blood Group" value={student.bloodgroup} />
                        </Stack>
                    </Stack>
                </Grid>

                <Grid className="col-sm-6" item xs={4.5} sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
                    <Stack spacing={1.5}>
                        <Stack spacing={1.5}>
                            <DataField label="Academic Year" value={activeYear} />
                        </Stack>
                    </Stack>
                    <Box sx={{
                        // p: 0.8,
                        // border: `1px solid ${THEME_GOLD}`,
                        borderRadius: '15px',
                        // bgcolor: '#fff',
                        boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
                        marginTop: 3
                    }}>
                        <QRCode
                            value={`STUDENT_ID:${student.id}`}
                            size={85}
                            fgColor="#333"
                        />
                    </Box>
                    <Typography sx={{ fontSize: '7px', fontWeight: 800, mt: 1, color: THEME_GOLD, letterSpacing: 0.5 }}>
                        OFFICIAL QR CODE
                    </Typography>
                </Grid>
            </Grid>
        </Box>

        {/* FOOTER WAVE DESIGN */}
        <Box sx={{
            position: 'absolute',
            bottom: 0,
            width: '100%',
            height: '70px',
            bgcolor: '#fdfdfd',
            borderTop: `2px solid ${THEME_GOLD}33`,
            p: 2,
            display: 'flex',
            alignItems: 'flex-end'
        }}>
            <Grid className="row" alignItems="flex-end">
                <Grid className="col-sm-10" item xs={8}>
                    <Stack spacing={0.6}>
                        <ContactRow
                            icon={<LocationOn sx={{ fontSize: 12 }} />}
                            text={student.address || "-"}
                        />
                        <ContactRow
                            icon={<Call sx={{ fontSize: 12 }} />}
                            text={student.fatherMobile}
                        />
                    </Stack>
                </Grid>
                <Grid className="col-sm-2" item xs={4} sx={{ textAlign: 'center' }}>
                    <Box sx={{
                        borderBottom: `1.5px solid ${THEME_RED}`,
                        mx: 'auto',
                        mb: 0.5,
                        height: '25px'
                    }} />
                    <Typography sx={{ fontSize: '9px', fontWeight: 900, color: THEME_RED, textTransform: 'uppercase' }}>
                        Principal
                    </Typography>
                </Grid>
            </Grid>
        </Box>
        <style>
            {`
              @media print {
  body * {
    visibility: hidden;
  }

  #print-area, #print-area * {
    visibility: visible;
  }

  #print-area {
    position: absolute;
    left: 0;
    top: 0;
    width: 100%;
    padding: 10px;
  }

  /* GRID LAYOUT FOR PRINT */
  .print-grid {
    display: grid !important;
    grid-template-columns: repeat(3, 1fr); /* 3 cards per row */
    gap: 15px;
    justify-items: center;
  }

  /* CARD SIZE CONTROL */
  .id-card {
    width: 300px !important;
    height: 500px !important;
    page-break-inside: avoid;
  }

  /* FORCE COLORS TO PRINT */
  * {
    -webkit-print-color-adjust: exact !important;
    print-color-adjust: exact !important;
  }
}
                `}
        </style>
    </Paper>

);

const DataField = ({ label, value }) => (
    <Box sx={{ borderLeft: `3px solid ${THEME_GOLD}`, pl: 1.5, py: 0.2 }}>
        <Typography sx={{ fontSize: '8px', color: THEME_GOLD, fontWeight: 900, textTransform: 'uppercase', letterSpacing: 0.8, lineHeight: 1 }}>
            {label}
        </Typography>
        <Typography sx={{ fontSize: '11.5px', color: '#333', fontWeight: 800, mt: 0.2 }}>
            {value || "---"}
        </Typography>
    </Box>
);

const ContactRow = ({ icon, text }) => (
    <Box sx={{ display: 'flex', alignItems: 'center', color: '#555' }}>
        <Box sx={{ color: THEME_RED, mr: 0.8, display: 'flex' }}>{icon}</Box>
        <Typography sx={{ fontSize: '8px', fontWeight: 700 }}>{text}</Typography>
    </Box>
);



export default Idcardgeneration;